import type { FieldData, QuoteInputs, QuoteCalculation, ComplexityScore, PricingBreakdown } from '../types';

export function calculateQuote(fieldData: FieldData, inputs: QuoteInputs): QuoteCalculation {
  // Calculate complexity score
  const complexity = calculateComplexity(fieldData, inputs);
  
  // Calculate pricing
  const pricing = calculatePricing(fieldData, inputs, complexity);
  
  // Estimate duration
  const estimatedDuration = estimateApplicationDuration(fieldData.acreage, complexity);
  
  return {
    inputs,
    fieldData,
    complexity,
    pricing,
    perAcreCost: pricing.finalRate,
    estimatedDuration,
  };
}

function calculateComplexity(fieldData: FieldData, inputs: QuoteInputs): ComplexityScore {
  // Shape complexity: analyze polygon perimeter-to-area ratio
  const shapeComplexity = calculateShapeComplexity(fieldData.polygon, fieldData.acreage);
  
  // Tree overhang factor
  const treeOverhangFactor = {
    'none': 0,
    'light': 0.15,
    'moderate': 0.35,
    'heavy': 0.60,
  }[inputs.treeOverhang];
  
  // Slope factor
  const slopeFactor = {
    'flat': 0,
    'gentle': 0.10,
    'moderate': 0.25,
    'steep': 0.45,
  }[inputs.hillsSlope];
  
  // Spacing factor (travel time between fields)
  const spacingFactor = inputs.multipleFields && inputs.fieldSpacing
    ? Math.min(inputs.fieldSpacing * 0.08, 0.30) // Cap at 30%
    : 0;
  
  // Overall complexity (weighted average)
  const overallComplexity = (
    shapeComplexity * 0.25 +
    treeOverhangFactor * 0.35 +
    slopeFactor * 0.25 +
    spacingFactor * 0.15
  );
  
  // Battery changes estimate
  const coveragePerBattery = 8; // acres per battery (conservative estimate)
  const batteryChangesEstimate = Math.ceil(fieldData.acreage / coveragePerBattery);
  
  return {
    shapeComplexity,
    treeOverhangFactor,
    slopeFactor,
    spacingFactor,
    overallComplexity,
    batteryChangesEstimate,
  };
}

function calculateShapeComplexity(polygon: Array<{ lat: number; lng: number }>, acreage: number): number {
  // Calculate perimeter
  let perimeter = 0;
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const dx = (polygon[j].lng - polygon[i].lng) * 111320 * Math.cos(polygon[i].lat * Math.PI / 180);
    const dy = (polygon[j].lat - polygon[i].lat) * 111320;
    perimeter += Math.sqrt(dx * dx + dy * dy);
  }
  
  // Calculate area in square meters
  const areaSqMeters = acreage * 4046.86;
  
  // Calculate circularity (1 = perfect circle, 0 = very irregular)
  const circularity = (4 * Math.PI * areaSqMeters) / (perimeter * perimeter);
  
  // Convert to complexity score (inverse of circularity)
  // 0 = simple/circular, 1 = very complex/irregular
  const complexity = Math.max(0, Math.min(1, 1 - circularity));
  
  return complexity;
}

function calculatePricing(fieldData: FieldData, inputs: QuoteInputs, complexity: ComplexityScore): PricingBreakdown {
  const acreage = fieldData.acreage;
  
  // Base rate: $20/acre for simple, flat fields
  const baseRate = 20;
  
  // Complexity adjustment: scale from $20 to $35 based on overall complexity
  const complexityAdjustedRate = baseRate + (complexity.overallComplexity * 15);
  
  // Seasonal demand adjustment
  const peakMonths = [5, 6, 7, 8]; // May-August
  const seasonalMultiplier = peakMonths.includes(inputs.applicationMonth) ? 1.10 : 1.0;
  const seasonalRate = complexityAdjustedRate * seasonalMultiplier;
  
  // Volume discount for large acreage
  let volumeDiscountRate = seasonalRate;
  if (acreage > 250) {
    // Gradually reduce rate as acreage increases
    // At 1000 acres, rate approaches base rate ($20) unless complexity is high
    const volumeFactor = Math.min((acreage - 250) / 750, 1); // 0 at 250 acres, 1 at 1000 acres
    const discountAmount = (seasonalRate - baseRate) * volumeFactor * 0.5; // 50% of the markup
    volumeDiscountRate = seasonalRate - discountAmount;
  }
  
  // Final rate per acre
  let finalRate = volumeDiscountRate;
  
  // Calculate subtotal
  const subtotal = finalRate * acreage;
  
  // 2-application commitment discount
  const twoAppDiscount = inputs.numberOfApplications === 2 ? subtotal * 0.10 : 0;
  
  // Total before minimum
  const totalBeforeMinimum = subtotal - twoAppDiscount;
  
  // Minimum charge: 75 acres × $20 = $1,500
  const minimumCharge = 75 * 20;
  
  // Final total
  let finalTotal = Math.max(totalBeforeMinimum, minimumCharge);
  
  // Internal adjustment: wooded acreage discount (not shown to user)
  let woodedAcreageAdjustment = 0;
  const estimatedWoodedAcres = acreage * complexity.treeOverhangFactor * 0.5; // rough estimate
  if (estimatedWoodedAcres > 30) {
    woodedAcreageAdjustment = finalTotal * 0.20; // 20% internal discount
    finalTotal = finalTotal - woodedAcreageAdjustment;
  }
  
  // Application type compliance factor
  const complianceFactor = getComplianceFactor(inputs.applicationType);
  if (complianceFactor > 0) {
    finalTotal = finalTotal * (1 + complianceFactor);
  }
  
  return {
    baseRate,
    complexityAdjustedRate,
    seasonalRate,
    volumeDiscountRate,
    finalRate,
    subtotal,
    twoAppDiscount,
    minimumCharge,
    totalBeforeMinimum,
    finalTotal,
    woodedAcreageAdjustment,
  };
}

function getComplianceFactor(applicationType: string): number {
  // Some application types require extra compliance/time
  const factors: Record<string, number> = {
    'herbicide': 0.05, // +5% for drift compliance
    'fungicide': 0,
    'insecticide': 0.08, // +8% for timing precision and compliance
    'foliar-nutrition': 0,
    'pgr': 0.10, // +10% for precise application requirements
  };
  
  return factors[applicationType] || 0;
}

function estimateApplicationDuration(acreage: number, complexity: ComplexityScore): string {
  // Base coverage rate: 15 acres/hour for simple fields
  const baseCoverageRate = 15;
  
  // Adjust for complexity
  const adjustedCoverageRate = baseCoverageRate * (1 - complexity.overallComplexity * 0.4);
  
  // Calculate hours
  const hours = acreage / adjustedCoverageRate;
  
  // Add setup and battery change time
  const setupTime = 0.5; // 30 minutes
  const batteryChangeTime = complexity.batteryChangesEstimate * 0.15; // 9 minutes per change
  const totalHours = hours + setupTime + batteryChangeTime;
  
  // Format as range
  const minHours = Math.floor(totalHours * 0.9);
  const maxHours = Math.ceil(totalHours * 1.1);
  
  if (minHours === maxHours) {
    return `${minHours} hour${minHours !== 1 ? 's' : ''}`;
  }
  
  return `${minHours}-${maxHours} hours`;
}
